import { databases, account } from '../lib/appwrite';
import { ID, Query } from 'appwrite';
import { getFoodItemById, getFoodImageUrl } from './foodItemService';
import { getFoodImages } from './foodImageService';
import { getProfileByUserId } from './profileService';
import { getPickupPointById } from './pickupPointService';

// ============================================
// CART SERVICE - SERVERLESS REDIS IMPLEMENTATION
// ============================================
// 
// Strategy:
// - All cart operations go through /api/cart endpoint
// - Redis credentials are kept server-side (secure)
// - Appwrite is used for initial load and final persist on checkout
//
// ============================================

const DATABASE_ID = process.env.REACT_APP_APPWRITE_DATABASE_ID;
const CART_COLLECTION_ID = process.env.REACT_APP_APPWRITE_CART_COLLECTION_ID;

// ============================================
// API HELPER
// ============================================

const cartApiUrl = '/api/cart';

/**
 * Call the cart API endpoint with JWT authentication
 */
const callCartApi = async (method, body = null, queryParams = null) => {
  let url = cartApiUrl;
  if (queryParams) {
    url += '?' + new URLSearchParams(queryParams).toString();
  }

  const options = {
    method,
    headers: { 
      'Content-Type': 'application/json',
    },
  };

  // 1. ADD JWT AUTHENTICATION
  try {
    const jwtResponse = await account.createJWT();
    options.headers['Authorization'] = `Bearer ${jwtResponse.jwt}`;
  } catch (authErr) {
    console.warn('[Cart API] Not authenticated; proceeding without JWT (Read-only or Browsing maybe)');
  }

  if (body) {
    options.body = JSON.stringify(body);
  }

  const response = await fetch(url, options);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `API error: ${response.status}`);
  }

  return response.json();
};

// ============================================
// INTERNAL APPWRITE FUNCTIONS (for sync only)
// ============================================

/**
 * Load cart items from Appwrite database (internal use)
 */
const loadFromAppwrite = async (customerId) => {
  try {
    if (!DATABASE_ID || !CART_COLLECTION_ID) {
      console.warn('Database or Cart Collection ID not configured');
      return [];
    }

    const response = await databases.listDocuments(
      DATABASE_ID,
      CART_COLLECTION_ID,
      [Query.equal('customer_id', customerId)]
    );

    console.log('[Appwrite READ] Cart items loaded:', response.documents.length);
    return response.documents;
  } catch (error) {
    const verboseError = `[Appwrite Client] Failed to load cart: ${error.message}`;
    console.error(verboseError, error);
    return [];
  }
};

/**
 * Save cart items to Appwrite database (internal use)
 */
const saveToAppwrite = async (customerId, cartItems) => {
  try {
    if (!DATABASE_ID || !CART_COLLECTION_ID) {
      console.warn('Database or Cart Collection ID not configured');
      return;
    }

    // First, delete all existing cart items for this customer
    const existing = await databases.listDocuments(
      DATABASE_ID,
      CART_COLLECTION_ID,
      [Query.equal('customer_id', customerId)]
    );

    for (const doc of existing.documents) {
      await databases.deleteDocument(DATABASE_ID, CART_COLLECTION_ID, doc.$id);
    }

    // Then create new cart items
    for (const item of cartItems) {
      await databases.createDocument(
        DATABASE_ID,
        CART_COLLECTION_ID,
        ID.unique(),
        {
          customer_id: customerId,
          food_id: item.food_id,
          quantity: item.quantity,
          added_at: item.added_at || new Date().toISOString(),
        }
      );
    }

    console.log('[Appwrite WRITE] Cart persisted:', cartItems.length, 'items');
  } catch (error) {
    const verboseError = `[Appwrite Client] Failed to save cart: ${error.message}`;
    console.error(verboseError, error);
    throw new Error(verboseError);
  }
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Enrich food item with vendor, images, and pickup details for caching
 */
const enrichFoodItemForCache = async (foodItem) => {
  const enrichedData = {
    $id: foodItem.$id,
    item_name: foodItem.item_name,
    price: foodItem.price,
    stock_count: foodItem.stock_count,
    vendor_id: foodItem.vendor_id,
    pickup_id: foodItem.pickup_id,
    cutoff_time: foodItem.cutoff_time,
    pickup_time: foodItem.pickup_time,
    hidden_after: foodItem.hidden_after,
  };

  // Fetch image
  try {
    const foodImages = await getFoodImages(foodItem.$id);
    if (foodImages.length > 0) {
      enrichedData.imageUrl = getFoodImageUrl(foodImages[0].imageId);
    }
  } catch (err) {
    console.warn('Could not fetch food images:', err);
    enrichedData.imageUrl = '';
  }

  // Fetch vendor name
  try {
    const vendorProfile = await getProfileByUserId(foodItem.vendor_id);
    enrichedData.vendorName = vendorProfile.vendor_name || vendorProfile.full_name || 'Vendor';
  } catch (err) {
    console.warn('Could not fetch vendor profile:', err);
    enrichedData.vendorName = 'Vendor';
  }

  // Fetch pickup location
  try {
    const pickupPoint = await getPickupPointById(foodItem.pickup_id);
    enrichedData.pickupLocation = pickupPoint.fullName || pickupPoint.name || 'Not specified';
  } catch (err) {
    console.warn('Could not fetch pickup point:', err);
    enrichedData.pickupLocation = 'Not specified';
  }

  return enrichedData;
};

// 2. SHARED CACHE ACTIONS
export const getVendorFromCache = async (customerId, vendorId) => {
  try {
    if (!customerId) return null;
    const result = await callCartApi('POST', { action: 'getVendor', customerId, vendorId });
    return result.cached ? result.data : null;
  } catch (error) {
    console.warn(`[Redis API] Failed to fetch vendor cache: ${error.message}`);
    return null;
  }
};

export const setVendorInCache = async (customerId, vendorId, vendorData) => {
  try {
    if (!customerId) return;
    await callCartApi('POST', { action: 'cacheVendor', customerId, vendorId, vendorData });
  } catch (error) {
    console.warn(`[Redis API] Failed to store vendor cache: ${error.message}`);
  }
};

// ============================================
// BACKGROUND SYNC (Debounced)
// ============================================

let syncTimeout = null;
const SYNC_DELAY = 30000; // 30 seconds inactivity
let currentCustomerId = null;
let lastAppwriteCheck = 0;
const APPWRITE_CHECK_INTERVAL = 30000; // 30 seconds throttle

/**
 * Triggers a debounced sync to Appwrite
 */
const triggerBackgroundSync = (customerId) => {
  if (!customerId) return;
  
  if (syncTimeout) {
    clearTimeout(syncTimeout);
  }
  
  syncTimeout = setTimeout(async () => {
    try {
      console.log('[Cart] 30s inactivity detected: Starting background sync to Appwrite...');
      await persistCartToAppwrite(customerId);
    } catch (error) {
      console.error('[Cart] Background sync failed:', error);
    }
  }, SYNC_DELAY);
};

/**
 * Setup instant sync listeners when the user leaves the site.
 * This ensures data is moved to Appwrite and cleared from Redis immediately.
 */
export const setupInstantSync = (customerId) => {
  if (!customerId || currentCustomerId === customerId) return;
  currentCustomerId = customerId;
  
  let isExiting = false;
  const performSync = () => {
    if (!currentCustomerId || isExiting) return;
    isExiting = true;
    
    console.log('[Cart] User exiting site: Triggering instant sync to Appwrite...');
    // We use fetch with keepalive: true to ensure it finishes even if the tab closes
    fetch('/api/cart', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'sync', customerId: currentCustomerId }),
      keepalive: true
    }).catch(err => console.error('[Cart] Instant sync fetch error:', err));
  };

  // Switch from visibilitychange (which fires on tab-switch) to pagehide/beforeunload (site exit)
  window.addEventListener('pagehide', performSync);
  window.addEventListener('beforeunload', performSync);
};

// ============================================
// PUBLIC CART API
// ============================================

/**
 * Load cart from Appwrite to Redis (called on session start/first access)
 * Supports multi-device sync by comparing timestamps.
 */
export const loadCartToRedis = async (customerId) => {
  try {
    if (!customerId) return;

    // 1. Get current Redis state
    const { loaded } = await callCartApi('POST', { action: 'isLoaded', customerId });
    
    // THROTTLING: If Redis is already loaded and we checked Appwrite recently, skip.
    const now = Date.now();
    if (loaded && (now - lastAppwriteCheck < APPWRITE_CHECK_INTERVAL)) {
      return;
    }

    lastAppwriteCheck = now;
    const redisCart = await callCartApi('GET', null, { customerId });
    const redisUpdatedAt = redisCart.updated_at ? new Date(redisCart.updated_at) : new Date(0);

    // 2. Get Appwrite state (check most recently updated item)
    const appwriteItems = await loadFromAppwrite(customerId);
    let appwriteUpdatedAt = new Date(0);
    
    if (appwriteItems.length > 0) {
      const timestamps = appwriteItems.map(item => new Date(item.$updatedAt || item.$createdAt).getTime());
      appwriteUpdatedAt = new Date(Math.max(...timestamps));
    }

    // 3. Multi-device logic:
    // If Appwrite is newer than Redis, or Redis hasn't been loaded this session,
    // then overwrite Redis with Appwrite data.
    if (!loaded || (appwriteUpdatedAt.getTime() > redisUpdatedAt.getTime() + 1000)) {
      console.log(`[Cart] Multi-device sync: Appwrite (${appwriteUpdatedAt.toISOString()}) is newer than Redis (${redisUpdatedAt.toISOString()})`);
      
      const redisCartItems = appwriteItems.map(item => ({
        id: item.$id,
        food_id: item.food_id,
        quantity: item.quantity,
        added_at: item.added_at || item.$createdAt,
      }));

      await callCartApi('POST', { action: 'load', customerId, items: redisCartItems });
    } else {
      console.log('[Cart] Redis is up to date with Appwrite (or newer)');
    }
  } catch (error) {
    console.error('Error in multi-device cart sync:', error);
  }
};

/**
 * Add an item to the cart
 */
export const addToCart = async (customerId, foodId, quantity = 1) => {
  try {
    console.log('[Cart] Adding to cart:', { customerId, foodId, quantity });

    if (!customerId) throw new Error('Customer ID is required');
    if (!foodId) throw new Error('Food ID is required');
    if (quantity <= 0) throw new Error('Quantity must be greater than 0');

    // OPTIMIZATION: We no longer perform redundant stock checks and food item 
    // fetches on the client. The server-side API handles all validation 
    // (stock check, vendor check) in one transactional request.
    
    // Ensure cart is loaded first (checks multi-device) - this is still needed 
    // once per session but the service handles it.
    await loadCartToRedis(customerId);

    // Add via API
    const result = await callCartApi('POST', { action: 'add', customerId, foodId, quantity });
    console.log('[Cart] Item added to Redis');
    
    // Trigger background sync
    triggerBackgroundSync(customerId);
    
    return result.item;
  } catch (error) {
    console.error('Error adding to cart:', error);
    throw error;
  }
};

/**
 * Get a specific cart item
 */
export const getCartItem = async (customerId, foodId) => {
  try {
    await loadCartToRedis(customerId);
    const { items } = await callCartApi('GET', null, { customerId });
    const item = items.find(item => item.food_id === foodId);
    return item ? { ...item, $id: item.id } : null;
  } catch (error) {
    console.error('Error getting cart item:', error);
    throw error;
  }
};

/**
 * Get all cart items for a customer
 */
export const getCartItems = async (customerId) => {
  try {
    if (!customerId) throw new Error('Customer ID is required');

    await loadCartToRedis(customerId);
    const { items } = await callCartApi('GET', null, { customerId });

    // Format for compatibility
    const formattedItems = items.map(item => ({
      ...item,
      $id: item.id,
    }));

    console.log('[Cart] Items fetched from Redis:', formattedItems.length);
    return formattedItems;
  } catch (error) {
    console.error('Error fetching cart items:', error);
    throw error;
  }
};

/**
 * Update cart item quantity (legacy - throws error)
 */
export const updateCartItemQuantity = async (cartItemId, quantity) => {
  throw new Error('updateCartItemQuantity requires customerId. Use updateCartItemQuantityWithCustomer instead.');
};

/**
 * Update cart item quantity (with customer ID)
 * Validates stock before allowing the update
 */
export const updateCartItemQuantityWithCustomer = async (customerId, cartItemId, quantity) => {
  try {
    // OPTIMIZATION: We no longer fetch the cart and food item on the client side
    // before making the update. The server-side API already performs stock 
    // validation and error checking. This reduces O(3) network calls down to O(1).
    
    const result = await callCartApi('POST', { action: 'update', customerId, cartItemId, quantity });
    console.log('[Cart] Item updated in Redis');
    
    // Trigger background sync
    triggerBackgroundSync(customerId);
    
    return result;
  } catch (error) {
    console.error('Error updating cart item:', error);
    throw error;
  }
};

/**
 * Remove an item from the cart (legacy - throws error)
 */
export const removeFromCart = async (cartItemId) => {
  throw new Error('removeFromCart requires customerId. Use removeFromCartWithCustomer instead.');
};

/**
 * Remove an item from the cart (with customer ID)
 */
export const removeFromCartWithCustomer = async (customerId, cartItemId) => {
  try {
    await callCartApi('POST', { action: 'remove', customerId, cartItemId });
    console.log('[Cart] Item removed from Redis');
    
    // Trigger background sync
    triggerBackgroundSync(customerId);
  } catch (error) {
    console.error('Error removing from cart:', error);
    throw error;
  }
};

/**
 * Clear all cart items for a customer
 */
export const clearCart = async (customerId) => {
  try {
    await callCartApi('POST', { action: 'clear', customerId });
    console.log('[Cart] Cleared in Redis');
    
    // Trigger background sync to eventually clear Appwrite
    triggerBackgroundSync(customerId);
  } catch (error) {
    console.error('Error clearing cart:', error);
    throw error;
  }
};

/**
 * Get cart item count for a customer
 */
export const getCartItemCount = async (customerId) => {
  try {
    await loadCartToRedis(customerId);
    const { items } = await callCartApi('GET', null, { customerId });
    return items.reduce((total, item) => total + (item.quantity || 0), 0);
  } catch (error) {
    console.error('Error getting cart count:', error);
    return 0;
  }
};

/**
 * Get cart total price
 */
export const getCartTotal = async (customerId) => {
  try {
    const cartItems = await getCartItems(customerId);

    let total = 0;
    for (const item of cartItems) {
      const foodItem = await getFoodItemById(item.food_id);
      if (foodItem) {
        total += foodItem.price * item.quantity;
      }
    }

    return total;
  } catch (error) {
    console.error('Error calculating cart total:', error);
    return 0;
  }
};

/**
 * Persist cart from Redis to Appwrite (called on checkout/session end)
 */
export const persistCartToAppwrite = async (customerId) => {
  try {
    const { items } = await callCartApi('GET', null, { customerId });

    if (items.length === 0) {
      // Clear Appwrite cart
      const existing = await databases.listDocuments(
        DATABASE_ID,
        CART_COLLECTION_ID,
        [Query.equal('customer_id', customerId)]
      );

      for (const doc of existing.documents) {
        await databases.deleteDocument(DATABASE_ID, CART_COLLECTION_ID, doc.$id);
      }

      console.log('[Appwrite WRITE] Cart cleared');
      return;
    }

    await saveToAppwrite(customerId, items);
    console.log('[Appwrite WRITE] Cart persisted');
  } catch (error) {
    console.error('Error persisting cart to Appwrite:', error);
    throw error;
  }
};

/**
 * Fetch cart items, validate them, remove invalid ones, and enrich with details.
 */
export const fetchAndValidateCartItems = async (customerId) => {
  try {
    await loadCartToRedis(customerId);
    const { items: cartItems } = await callCartApi('GET', null, { customerId });

    if (cartItems.length === 0) {
      return { items: [], removedCount: 0 };
    }

    const itemsToRemove = [];
    const enrichedItems = [];

    for (const cartItem of cartItems) {
      try {
        let foodItem = null;
        let isCached = false;

        // 1. TRY CACHE FIRST (TRUE CACHE-FIRST)
        try {
          const cacheResult = await callCartApi('POST', { action: 'getFood', customerId, foodId: cartItem.food_id });
          if (cacheResult.cached && cacheResult.data) {
            console.log('[Cart] Cache hit on Validation! Skipping Appwrite for:', cartItem.food_id);
            foodItem = cacheResult.data;
            isCached = true;
          }
        } catch (cacheErr) {
          console.warn('[Cart] Cache check failed, falling back to DB:', cacheErr);
        }

        // 2. FALLBACK TO DATABASE IF NOT CACHED
        if (!foodItem) {
          try {
            console.log('[Cart] Cache miss on Validation, fetching from Appwrite:', cartItem.food_id);
            foodItem = await getFoodItemById(cartItem.food_id);
          } catch (err) {
            if (err.code === 404) {
              console.log('Food item deleted, marking for removal:', cartItem.food_id);
              itemsToRemove.push({ id: cartItem.id, reason: 'Item deleted' });
              continue;
            }
            throw err;
          }
        }

        // 3. VALIDATION CHECKS (Works on both Fresh and Cached data)
        const now = new Date();
        let isInvalid = false;
        let removeReason = '';

        if ((foodItem.stock_count || 0) <= 0) {
          isInvalid = true;
          removeReason = 'Out of stock';
        }

        if (foodItem.cutoff_time) {
          const cutoffDate = new Date(foodItem.cutoff_time);
          if (cutoffDate < now) {
            isInvalid = true;
            removeReason = 'Past cutoff time';
          }
        }

        if (foodItem.hidden_after) {
          const hiddenDate = new Date(foodItem.hidden_after);
          if (hiddenDate < now) {
            isInvalid = true;
            removeReason = 'Item is no longer available';
          }
        }

        if (isInvalid) {
          console.log(`Marking invalid item for removal (${removeReason}):`, foodItem.item_name);
          itemsToRemove.push({ id: cartItem.id, reason: removeReason });
          continue;
        }

        // 4. ENRICHMENT - We already have all data if it was cached!
        let imageUrl = foodItem.imageUrl || '';
        let vendorName = foodItem.vendorName || 'Vendor';
        let pickupLocation = foodItem.pickupLocation || 'Not specified';
        
        if (!isCached) {
          // Cache miss enrichment - fetch missing pieces if any (though getFoodItemById provides base)
          // and then cache the full result
          console.log('[Cart] Enriching and caching persistent data for other users:', cartItem.food_id);
          
          const enrichedData = await enrichFoodItemForCache(foodItem);
          imageUrl = enrichedData.imageUrl;
          vendorName = enrichedData.vendorName;
          pickupLocation = enrichedData.pickupLocation;

          try {
            await callCartApi('POST', { action: 'cacheFood', customerId, foodId: cartItem.food_id, foodData: enrichedData });
          } catch (cacheErr) {
            console.warn('[Cart] Could not update food cache:', cacheErr);
          }
        }

        // Format dates
        const cutoffDate = new Date(foodItem.cutoff_time || null);
        const orderBy = cutoffDate && !isNaN(cutoffDate.getTime()) ? (
          cutoffDate.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }) + ', ' +
          cutoffDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
        ) : 'Not specified';

        const pickupDate = new Date(foodItem.pickup_time || null);
        const pickupBy = pickupDate && !isNaN(pickupDate.getTime()) ? (
          pickupDate.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }) + ', ' +
          pickupDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
        ) : 'Not specified';

        enrichedItems.push({
          id: cartItem.id,
          foodId: foodItem.$id,
          name: foodItem.item_name,
          price: foodItem.price,
          quantity: cartItem.quantity,
          image: imageUrl,
          vendor: vendorName,
          vendorId: foodItem.vendor_id,
          pickupId: foodItem.pickup_id,
          stockCount: foodItem.stock_count,
          pickupTime: foodItem.pickup_time,
          cutoffTime: foodItem.cutoff_time,
          orderBy: orderBy,
          pickupBy: pickupBy,
          pickupLocation: pickupLocation
        });

      } catch (itemError) {
        console.error('Error processing cart item:', itemError);
      }
    }

    // Remove invalid items via API
    let actuallyRemovedCount = 0;
    for (const item of itemsToRemove) {
      try {
        await callCartApi('POST', { action: 'remove', customerId, cartItemId: item.id });
        actuallyRemovedCount++;
      } catch (err) {
        console.error('Error removing invalid item:', err);
      }
    }

    return {
      items: enrichedItems,
      removedCount: actuallyRemovedCount
    };

  } catch (error) {
    const verboseError = `[Cart Validation Error] ${error.message}`;
    console.error(verboseError, error);
    throw new Error(verboseError);
  }
};

/**
 * Explicitly clear a food item's cache in Redis.
 * Useful after a stock update to ensure the next load is fresh.
 */
export const clearFoodCacheInRedis = async (customerId, foodId) => {
  try {
    if (!customerId) return;
    await callCartApi('POST', { action: 'clearFoodCache', customerId, foodId });
    console.log('[Redis] Shared cache purged for food:', foodId);
  } catch (error) {
    console.warn(`[Redis API] Failed to purge food cache: ${error.message}`);
  }
};
